import { useEffect, useRef, useState } from 'react';
import { useRoute, useLocation } from 'wouter';
import {
  Button,
  Card,
  CardBody,
  Image,
  Tooltip,
  Spinner,
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Input,
  Textarea,
} from '@nextui-org/react';
import { toast } from 'react-toastify';
import DOMPurify from 'dompurify';
import {
  getReportBySlug,
  trackReportView,
  trackReportShare,
  getReportApplauseCount,
  addReportApplause,
} from 'common/src/api/reports';
import { Report } from 'common/src/utils/types';
import useAuthState from 'common/src/hooks/useAuthState';
import useUserStore from 'common/src/stores/useUserStore';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import useBrand from 'common/src/hooks/useBrand';

const ReportViewPage = () => {
  const [, params] = useRoute('/reports/:slug');
  const slug = params?.slug;
  const [, setLocation] = useLocation();
  const { session } = useAuthState();
  const { userProfile } = useUserStore();
  const { assets: brandAssets } = useBrand();

  const [report, setReport] = useState<Report | null>(null);
  const [loading, setLoading] = useState(true);
  const [accessDenied, setAccessDenied] = useState(false);
  const [scrollProgress, setScrollProgress] = useState(0);
  const [applauseCount, setApplauseCount] = useState(0);
  const [hasApplauded, setHasApplauded] = useState(false);
  const [openDemoModal, setOpenDemoModal] = useState(false);
  const [demoName, setDemoName] = useState('');
  const [demoEmail, setDemoEmail] = useState('');
  const [demoCompany, setDemoCompany] = useState('');
  const [demoMessage, setDemoMessage] = useState('');
  const applauseBtnRef = useRef<HTMLButtonElement | null>(null);

  const fireConfetti = () => {
    try {
      const btn = applauseBtnRef.current;
      const rect = btn?.getBoundingClientRect();
      const originX = rect ? rect.left + rect.width / 2 : window.innerWidth / 2;
      const originY = rect
        ? rect.top + window.scrollY
        : window.scrollY + window.innerHeight / 2;
      const fruits = ['🍒', '🍑', '🫐', '🍇', '🥝', '🍊', '🍎', '🍐', '🥑', '🍋'];
      const pieces = 14;
      const first = Math.floor(Math.random() * fruits.length);
      for (let i = 0; i < pieces; i++) {
        const el = document.createElement('span');
        el.className = 'confetti-piece';
        el.textContent = fruits[(first + i) % fruits.length];
        // Fan out upwards between -160° and -20°, then fall (see index.css).
        const angle =
          ((-160 + (140 * i) / (pieces - 1) + (Math.random() - 0.5) * 12) *
            Math.PI) /
          180;
        const distance = 120 + Math.random() * 110;
        const spin = (Math.random() > 0.5 ? 1 : -1) * (180 + Math.random() * 360);
        el.style.left = `${originX - 12}px`;
        el.style.top = `${originY}px`;
        el.style.setProperty('--dx', `${Math.cos(angle) * distance}px`);
        el.style.setProperty('--dy', `${Math.sin(angle) * distance}px`);
        el.style.setProperty('--rot', `${spin}deg`);
        el.style.fontSize = `${20 + Math.random() * 12}px`;
        el.style.animationDelay = `${Math.random() * 0.08}s`;
        document.body.appendChild(el);
        window.setTimeout(() => el.remove(), 1300);
      }
    } catch {}
  };

  const loadedForSlugRef = useRef<string | null>(null);

  useEffect(() => {
    if (!slug) return;

    // Avoid refetch/flicker on auth/profile changes for the same slug
    if (loadedForSlugRef.current === slug) return;

    const loadReport = async () => {
      setLoading(true);
      try {
        const data = await getReportBySlug(slug);

        if (!data) {
          toast.error('Report not found');
          setAccessDenied(true);
          setLoading(false);
          return;
        }

        // Check access for private reports
        if (data.visibility === 'private') {
          if (!session || !userProfile) {
            setAccessDenied(true);
            setLoading(false);
            return;
          }

          // Check if user has access
          const hasAccess =
            data.target_audience.includes('all') ||
            data.target_audience.includes(
              userProfile.exporter_id?.toString() || ''
            );

          if (!hasAccess) {
            setAccessDenied(true);
            setLoading(false);
            return;
          }
        }

        // For open reports, allow access without authentication
        setReport(data);
      } catch (error) {
        console.error('Error loading report:', error);
        toast.error('Failed to load report');
      } finally {
        setLoading(false);
        loadedForSlugRef.current = slug;
      }
    };

    loadReport();
  }, [slug]);

  // Ensure we only track one view per slug load, with a small delay if session exists to allow userProfile to load
  const hasTrackedRef = useRef(false);
  const fallbackTimerRef = useRef<number | null>(null);

  // Reset tracking flag when slug changes
  useEffect(() => {
    hasTrackedRef.current = false;
  }, [slug]);

  // Load applause count when report is ready
  useEffect(() => {
    const loadApplause = async () => {
      if (!report) return;
      const count = await getReportApplauseCount(report.id);
      setApplauseCount(count);
      setHasApplauded(false);
    };
    loadApplause();
  }, [report]);

  // Track view once when report is available (and prefer including user id if session is present)
  useEffect(() => {
    if (!report || hasTrackedRef.current) return;

    const doTrack = (uid: number | null) => {
      hasTrackedRef.current = true;
      trackReportView(report.id, uid, null, navigator.userAgent).catch((err) =>
        console.error('Error tracking view:', err)
      );
    };

    // If no session, track immediately as anonymous
    if (!session) {
      doTrack(null);
      return;
    }

    // Session exists: try to include user id if available
    if (userProfile?.id) {
      doTrack(userProfile.id);
      return;
    }

    // Session but userProfile not yet loaded: wait briefly, then fallback to anonymous
    fallbackTimerRef.current = window.setTimeout(() => {
      if (!hasTrackedRef.current && report) {
        doTrack(null);
      }
    }, 1500);

    return () => {
      if (fallbackTimerRef.current) {
        clearTimeout(fallbackTimerRef.current);
        fallbackTimerRef.current = null;
      }
    };
  }, [report, session, userProfile?.id]);

  // Reading progress based on page scroll
  useEffect(() => {
    const calcProgress = () => {
      const scrollTop = window.scrollY || document.documentElement.scrollTop;
      const docHeight = document.documentElement.scrollHeight;
      const winHeight = window.innerHeight;
      const total = Math.max(docHeight - winHeight, 1);
      const pct = Math.min(100, Math.max(0, (scrollTop / total) * 100));
      setScrollProgress(pct);
    };
    calcProgress();
    window.addEventListener('scroll', calcProgress, { passive: true });
    window.addEventListener('resize', calcProgress);
    return () => {
      window.removeEventListener('scroll', calcProgress);
      window.removeEventListener('resize', calcProgress);
    };
  }, []);

  const handleShare = async () => {
    if (!report) return;

    const url = window.location.href;

    try {
      await navigator.clipboard.writeText(url);
      toast.success('Link copiado! Compártelo!');

      // Track share (include user id when available)
      await trackReportShare(
        report.id,
        session ? userProfile?.id || null : null,
        'link'
      );
    } catch (error) {
      console.error('Failed to copy link:', error);
      toast.error('Failed to copy link');
    }
  };

  if (loading) {
    return (
      <div className='h-screen w-screen flex flex-col items-center justify-center gap-6 bg-background'>
        <Image
          src={brandAssets.collapsedLight}
          alt={brandAssets.name}
          width={120}
          height={120}
        />
        <Spinner size='lg' color='secondary' label='Cargando reporte…' />
      </div>
    );
  }

  if (accessDenied) {
    return (
      <div className='min-h-screen flex items-center justify-center p-4 bg-default-100'>
        <Card shadow='sm' className='max-w-md'>
          <CardBody className='text-center space-y-4'>
            {/* design-ignore: pantalla de acceso denegado de una página pública */}
            <div className='text-6xl'>🔒</div>
            {/* design-ignore: pantalla de acceso denegado de una página pública */}
            <h2 className='text-2xl font-bold'>Acceso requerido</h2>
            <p className='text-default-500'>
              {report
                ? 'Este reporte es privado. Inicia sesión con una cuenta autorizada para verlo.'
                : 'Este reporte no está disponible o requiere autenticación.'}
            </p>
            <Button
              color='primary'
              onPress={() => (window.location.href = '/')}
            >
              Ir al inicio de sesión
            </Button>
          </CardBody>
        </Card>
      </div>
    );
  }

  if (!report) {
    return null;
  }

  // Sanitize HTML content
  const sanitizedContent = DOMPurify.sanitize(report.content, {
    ALLOWED_TAGS: [
      'p',
      'br',
      'strong',
      'em',
      'u',
      's',
      'h1',
      'h2',
      'h3',
      'h4',
      'h5',
      'h6',
      'ul',
      'ol',
      'li',
      'a',
      'img',
      'blockquote',
      'hr',
      'code',
      'pre',
    ],
    ALLOWED_ATTR: ['href', 'src', 'alt', 'class', 'target', 'rel'],
  });

  return (
    <div className='fruitmarkets-theme min-h-screen bg-default-100'>
      {/* Reading progress bar: logo colors spread across the screen width */}
      <div className='fixed top-0 left-0 right-0 h-1 bg-default-200/60 z-40'>
        <div
          className='h-full bg-gradient-to-r from-fruitmarkets-pink via-fruitmarkets-purple to-fruitmarkets-green bg-[length:100vw_100%] transition-[width] duration-150'
          style={{ width: `${scrollProgress}%` }}
        />
      </div>
      {/* Header */}
      <div className='bg-background border-b'>
        {/* Top bar: logo left, share right */}
        <div className='container mx-auto px-4 lg:px-8 py-4'>
          <div className='flex items-center justify-between'>
            <button
              type='button'
              onClick={() => setLocation('/')}
              className='flex items-center gap-3 hover:opacity-90'
            >
              <img
                src={brandAssets.fullLogoLight}
                alt={brandAssets.name}
                className='h-8 w-auto'
              />
            </button>
            <Tooltip content='Copiar enlace' placement='bottom'>
              <Button
                size='lg'
                color='primary'
                onPress={handleShare}
                aria-label='Compartir reporte'
                startContent={
                  <svg
                    xmlns='http://www.w3.org/2000/svg'
                    viewBox='0 0 24 24'
                    fill='currentColor'
                    className='w-4 h-4'
                  >
                    <path d='M13 5.828V15a1 1 0 1 1-2 0V5.828L8.414 8.414A1 1 0 0 1 7 7l5-5 5 5a1 1 0 1 1-1.414 1.414L13 5.828z' />
                    <path d='M5 13a1 1 0 0 1 1 1v4h12v-4a1 1 0 1 1 2 0v5a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1v-5a1 1 0 0 1 1-1z' />
                  </svg>
                }
              >
                Compartir
              </Button>
            </Tooltip>
          </div>
        </div>
        {/* Title + meta centered */}
        <div className='container mx-auto px-4 lg:px-8 py-8'>
          {/* design-ignore: título de la página pública del reporte */}
          <h1 className='text-center text-3xl lg:text-5xl font-extrabold text-foreground tracking-tight'>
            {report.title}
          </h1>
          <div className='mt-5 flex items-center justify-center gap-3 text-sm text-default-500'>
            <p className='text-lg'>{'Equipo FruitMarkets'}</p>
            {report.published_at && <span>•</span>}
            {report.published_at && (
              <p className='text-lg'>
                {format(new Date(report.published_at), 'dd MMMM yyyy', {
                  locale: es,
                })}
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className='container mx-auto px-4 lg:px-8 py-10'>
        {/* Sticky share bar (desktop) */}
        <div className='hidden lg:flex fixed right-6 top-1/3 z-30 flex-col gap-2'>
          <Tooltip content='Compartir enlace' placement='left'>
            <Button
              isIconOnly
              variant='flat'
              className='bg-background shadow-md border border-divider'
              onPress={handleShare}
            >
              📤
            </Button>
          </Tooltip>
          <Tooltip content='Volver arriba' placement='left'>
            <Button
              isIconOnly
              variant='flat'
              className='bg-background shadow-md border border-divider'
              onPress={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
            >
              ⬆️
            </Button>
          </Tooltip>
        </div>
        <Card shadow='sm' className='max-w-4xl mx-auto'>
          <CardBody className='p-6 lg:p-12'>
            <div
              className='prose prose-sm sm:prose lg:prose-lg xl:prose-xl max-w-none'
              dangerouslySetInnerHTML={{ __html: sanitizedContent }}
            />
            {/* Applause section */}
            <div className='mt-10 flex flex-col items-center gap-3'>
              <Button
                radius='full'
                size='lg'
                color={hasApplauded ? 'secondary' : 'default'}
                variant={hasApplauded ? 'solid' : 'flat'}
                className='w-16 h-16 text-2xl'
                ref={applauseBtnRef}
                onPress={async () => {
                  if (!report) return;
                  if (!hasApplauded) setHasApplauded(true);
                  setApplauseCount((c) => c + 1);
                  const result = await addReportApplause(
                    report.id,
                    session ? userProfile?.id || null : null
                  );
                  if (result.ok) {
                    fireConfetti();
                  } else {
                    // rollback on failure
                    // keep hasApplauded if they had clapped before in this session
                    setApplauseCount((c) => Math.max(0, c - 1));
                    toast.error('No se pudo registrar tu aplauso');
                  }
                }}
              >
                👏
              </Button>
              <div className='text-sm text-default-500'>
                <span className='font-semibold text-foreground'>
                  {applauseCount}
                </span>{' '}
                aplausos
              </div>
            </div>
          </CardBody>
        </Card>

        {/* Footer CTA */}
        <div className='max-w-4xl mx-auto mt-12'>
          <Card shadow='sm' className='overflow-hidden'>
            <CardBody className='p-0'>
              <div className='h-1.5 bg-gradient-to-r from-fruitmarkets-pink via-fruitmarkets-purple to-fruitmarkets-green' />
              <div className='p-8 lg:p-10'>
                <div className='flex flex-col lg:flex-row items-start lg:items-center justify-between gap-6'>
                  <div>
                    <img
                      src={brandAssets.fullLogoLight}
                      alt={brandAssets.name}
                      className='h-8 w-auto dark:hidden'
                    />
                    <img
                      src={brandAssets.fullLogoDark}
                      alt={brandAssets.name}
                      className='hidden h-8 w-auto dark:block'
                    />
                    {/* design-ignore: código comentado */}
                    {/* <div className='text-3xl mb-2'>🍒</div> */}
                    <h3 className='text-2xl font-extrabold leading-tight mt-4'>
                      Inteligencia comercial
                      <br />
                      para exportadoras de fruta.
                    </h3>
                    <p className='text-default-600 mt-2 max-w-2xl'>
                      Revisa tendencias de mercado. Evalúa el desempeño de tus
                      recibidores. Compara tus resultados con los de la
                      industria.
                    </p>
                  </div>
                  <div className='flex items-center gap-3 w-full lg:w-auto'>
                    {!session ? (
                      <Button
                        color='primary'
                        className='font-semibold'
                        onPress={() => setLocation('/')}
                      >
                        Iniciar sesión
                      </Button>
                    ) : (
                      <Button
                        color='primary'
                        className='font-semibold'
                        onPress={() => setLocation('/')}
                      >
                        Ir a FruitMarkets
                      </Button>
                    )}
                    <Button
                      variant='flat'
                      className='font-semibold'
                      onPress={() => {
                        setOpenDemoModal(true);
                      }}
                    >
                      Agendar una Demo
                    </Button>
                    {/* design-ignore: botón comentado, mismo caso del banner con degradado de marca */}
                    {/* <Button
                      variant='bordered'
                      className='border-white text-white'
                      onPress={() =>
                        window.scrollTo({ top: 0, behavior: 'smooth' })
                      }
                    >
                      Volver arriba
                    </Button> */}
                  </div>
                </div>
              </div>
              {/* <div className='flex items-center justify-between px-6 py-3 text-xs text-default-500 bg-background'>
                <span>© {new Date().getFullYear()} CherryMarket</span>
                <span className='hidden sm:inline'>
                  Todos los derechos reservados
                </span>
              </div> */}
            </CardBody>
          </Card>
        </div>
      </div>
      <Modal
        isOpen={openDemoModal}
        onClose={() => setOpenDemoModal(false)}
        size='lg'
      >
        <ModalContent>
          {(onClose) => (
            <>
              <ModalHeader className='flex flex-col items-start gap-1'>
                {/* design-ignore: título dentro de un modal */}
                <h2 className='text-2xl font-bold mt-2'>
                  Agenda una demo con Juan Pablo
                </h2>
                <p className='text-sm text-default-500'>
                  Conoce cómo FruitMarkets ayuda a exportadoras a tomar mejores
                  decisiones con datos en tiempo real.
                </p>
              </ModalHeader>
              <ModalBody className='space-y-4'>
                <p>
                  Escríbele un correo a jp@fruitmarkets.com para agendar una
                  reunión.
                </p>
                {/* <div className='grid grid-cols-1 md:grid-cols-2 gap-3'>
                  <Input
                    label='Nombre'
                    placeholder='Tu nombre'
                    value={demoName}
                    onValueChange={setDemoName}
                  />
                  <Input
                    type='email'
                    label='Email'
                    placeholder='tu@empresa.com'
                    value={demoEmail}
                    onValueChange={setDemoEmail}
                  />
                </div>
                <Input
                  label='Exportadora'
                  placeholder='Nombre de tu empresa'
                  value={demoCompany}
                  onValueChange={setDemoCompany}
                />
                <Textarea
                  label='¿Qué te gustaría ver?'
                  placeholder='Cuéntanos brevemente qué te interesa analizar o mejorar'
                  minRows={3}
                  value={demoMessage}
                  onValueChange={setDemoMessage}
                /> */}
              </ModalBody>
              <ModalFooter className='justify-end'>
                <div className='flex gap-2'>
                  <Button variant='flat' onPress={onClose}>
                    Cerrar
                  </Button>
                  {/* <Button
                    color='primary'
                    onPress={() => {
                      const subject = `Solicitud demo CherryMarket - ${demoCompany || demoName || 'sin empresa'}`;
                      const body = [
                        `Hola equipo CherryMarket,`,
                        '',
                        `Me gustaría agendar una demo.`,
                        `Nombre: ${demoName || '—'}`,
                        `Email: ${demoEmail || '—'}`,
                        `Exportadora: ${demoCompany || '—'}`,
                        '',
                        `Intereses: ${demoMessage || '—'}`,
                      ].join('%0D%0A');
                      const mailto = `mailto:jp@cherrymarket.cl?subject=${encodeURIComponent(subject)}&body=${body}`;
                      window.location.href = mailto;
                    }}
                  >
                    Enviar solicitud
                  </Button> */}
                </div>
              </ModalFooter>
            </>
          )}
        </ModalContent>
      </Modal>
    </div>
  );
};

export default ReportViewPage;
